import React, { useEffect, useState } from "react";
import api from "../services/api";  // Zakładam, że masz już axios skonfigurowane w api.js

const safeText = (val) => {
  if (val === null || val === undefined) return "";
  if (typeof val === "string" || typeof val === "number" || typeof val === "boolean") return String(val);
  if (typeof val === "object") {
    if (val.email) return val.email;
    if (val._id) return String(val._id);
    if (val.id) return String(val.id);
    try {
      const s = JSON.stringify(val);
      return s.length > 120 ? s.slice(0, 117) + "..." : s;
    } catch {
      return "[object]";
    }
  }
  return String(val);
};

const AdminPage = () => {
  const [users, setUsers] = useState([]);
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      const usersRes = await api.get("/admin/users");
      const shopsRes = await api.get("/admin/shops");

      const u = Array.isArray(usersRes.data) ? usersRes.data : usersRes.data?.users || [];
      const s = Array.isArray(shopsRes.data) ? shopsRes.data : shopsRes.data?.shops || [];

      setUsers(u);
      setShops(s);
    } catch (err) {
      console.error("fetchData error:", err);
      setError("Nie udało się pobrać danych.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const getKey = (item) => {
    return item?._id ?? item?.id ?? `${Math.random().toString(36).slice(2, 9)}`;
  };

  // Funkcje do aktualizacji sklepu na backendzie
  const handleBlockShop = async (shopId) => {
    try {
      const response = await api.put(`/admin/shops/${shopId}/block`);
      setShops((prevShops) => 
        prevShops.map((shop) =>
          shop._id === shopId ? { ...shop, isBlocked: true } : shop
        )
      );
    } catch (err) {
      console.error("Error blocking shop:", err);
    }
  };

  const handleUnblockShop = async (shopId) => {
    try {
      const response = await api.put(`/admin/shops/${shopId}/unblock`);
      setShops((prevShops) => 
        prevShops.map((shop) =>
          shop._id === shopId ? { ...shop, isBlocked: false } : shop
        )
      );
    } catch (err) {
      console.error("Error unblocking shop:", err);
    }
  };

  const handleSetBetaTest = async (shopId) => {
    try {
      const response = await api.put(`/admin/shops/${shopId}/set-beta-test`);
      setShops((prevShops) => 
        prevShops.map((shop) =>
          shop._id === shopId ? { ...shop, isBetaTest: true } : shop
        )
      );
    } catch (err) {
      console.error("Error setting beta test:", err);
    }
  };

  const handleVerifyShop = async (shopId) => {
    try {
      const response = await api.put(`/admin/shops/${shopId}/verify`);
      setShops((prevShops) => 
        prevShops.map((shop) =>
          shop._id === shopId ? { ...shop, isVerified: true } : shop
        )
      );
    } catch (err) {
      console.error("Error verifying shop:", err);
    }
  };

  if (loading) return <p>Ładowanie…</p>;
  if (error) return <p className="text-red-500">{error}</p>;

  return (
    <div>
      <h1 className="text-3xl font-bold mb-4">Panel Administratora</h1>

      {/* UŻYTKOWNICY */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold mb-2">Użytkownicy</h2>
        {users.length === 0 ? (
          <p>Brak użytkowników.</p>
        ) : (
          <ul className="space-y-2">
            {users.map((u, idx) => {
              const key = getKey(u) + "-user-" + idx;
              return (
                <li
                  key={key}
                  className="p-3 bg-gray-100 text-black rounded flex justify-between items-center"
                >
                  <div>
                    <p><strong>ID:</strong> {safeText(u._id ?? u.id)}</p>
                    <p><strong>Email:</strong> {safeText(u.email)}</p>
                    <p><strong>Rola:</strong> {safeText(u.role)}</p>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* SKLEPY */}
      <div>
        <h2 className="text-2xl font-bold mb-2">Sklepy</h2>

        {shops.length === 0 ? (
          <p>Brak sklepów.</p>
        ) : (
          <ul className="space-y-2">
            {shops.map((s, idx) => {
              const key = getKey(s) + "-shop-" + idx;
              return (
                <li
                  key={key}
                  className="p-3 bg-gray-100 text-black rounded flex justify-between items-center"
                >
                  <div>
                    <p><strong>ID:</strong> {safeText(s._id ?? s.id)}</p>
                    <p><strong>Nazwa:</strong> {safeText(s.name)}</p>
                    <p><strong>Właściciel:</strong> {safeText(s.owner)}</p>
                    <p><strong>Status:</strong> {s.isBlocked ? "Zablokowany" : "Aktywny"}</p>
                    <p><strong>Beta Test:</strong> {s.isBetaTest ? "Tak" : "Nie"}</p>
                    <p><strong>Weryfikacja:</strong> {s.isVerified ? "Zweryfikowany" : "Niezwerifikowany"}</p>

                    <div className="space-x-2 mt-2">
                      {s.isBlocked ? (
                        <button
                          className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded"
                          onClick={() => handleUnblockShop(s._id)}
                        >
                          Odblokuj
                        </button>
                      ) : (
                        <button
                          className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded"
                          onClick={() => handleBlockShop(s._id)}
                        >
                          Zablokuj
                        </button>
                      )}
                      <button
                        className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded"
                        onClick={() => handleSetBetaTest(s._id)}
                      >
                        Ustaw na Beta Test
                      </button>
                      <button
                        className="px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded"
                        onClick={() => handleVerifyShop(s._id)}
                      >
                        Weryfikuj sklep
                      </button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default AdminPage;